import React, { useContext, useState, useEffect, useRef } from 'react';
import { Handle, Position, useStore } from 'reactflow';
import './NodeComponent.scss';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Icon, DropdownMenu, Input, Tooltip } from '@hydrodigital/design-system';
dayjs.extend(relativeTime);

const NodeComponent: React.FC = ({ data, id }: any) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const previousTransformRef = useRef<[number, number, number] | null>(null);
  const transform = useStore((state) => state.transform);

  useEffect(() => {
    if (previousTransformRef.current !== null) {
      const [prevX, prevY, prevZoom] = previousTransformRef.current;
      if (prevX !== transform[0] || prevY !== transform[1] || prevZoom !== transform[2]) {
        setDropdownOpen(false);
      }
    }
    previousTransformRef.current = [transform[0], transform[1], transform[2]];
  }, [transform]);

  return (
    <>
      <div
        className="parameter-node py-2"
        style={{
          border: 'solid 2px #E8EAED',
          borderRadius: '12px',
          backgroundColor: '#FFFFFF',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Handle
          type="target"
          position={Position.Left}
          id="t1"
          style={{
            top: '30px',
            left: '-10px',
            backgroundColor: 'transparent',
            width: '10px',
            height: '10px',
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent',
            borderLeft: '10px solid #020202',
            borderRadius: '0px',
          }}
        />
        <Handle
          type="source"
          position={Position.Left}
          id="s1"
          style={{
            top: '110px',
            left: '-10px',
            backgroundColor: 'transparent',
            width: '10px',
            height: '10px',
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent',
            borderRight: '10px solid #020202',
            borderRadius: '0px',
          }}
        />
        <Handle
          type="target"
          position={Position.Top}
          id="t2"
          style={{
            left: '70px',
            top: '-10px',
            backgroundColor: 'transparent',
            width: '10px',
            height: '10px',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '10px solid #020202',
            borderRadius: '0px',
          }}
        />
        <Handle
          type="source"
          position={Position.Top}
          id="s2"
          style={{
            left: '215px',
            top: '-10px',
            backgroundColor: 'transparent',
            width: '10px',
            height: '10px',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: '10px solid #020202',
            borderRadius: '0px',
          }}
        />
        <div className="flex flex-column">
          <div
            className="flex items-center"
            style={{ padding: '0px 15px', gap: '16px', justifyContent: 'space-between' }}
          >
            <Icon name="sync" size="m" style={{ color: 'black', flexShrink: 0 }} />
            <Input
              size="small"
              placeholder="Mode Name"
              value={data.label}
              onChange={(event) => {
                data.onChangeLabel(event.target.value, id);
              }}
              style={{ flex: 1 }}
            />
            <div style={{ display: 'inline-flex', flexShrink: 0 }}>
              <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenu.Trigger style={{ padding: 0, minWidth: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="ellipsis-v" size="m" />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  {data.initial && (
                    <>
                      <DropdownMenu.Item onClick={() => data.removeInitialState(id)}>
                        <Icon name="times-circle" size="s" style={{ marginRight: '8px' }} />
                        Remove Initial State
                      </DropdownMenu.Item>
                      <DropdownMenu.Separator />
                    </>
                  )}

                  {data.initial && (
                    <>
                      <DropdownMenu.Item onClick={() => data.setDisabled(id)}>
                        <Icon name="play-circle" size="s" style={{ marginRight: '8px' }} />
                        Disable
                      </DropdownMenu.Item>
                      <DropdownMenu.Separator />
                    </>
                  )}

                  {!data.initial && data.enabled && (
                    <>
                      <DropdownMenu.Item onClick={() => data.setInitial(id)}>
                        <Icon name="windsock" size="s" style={{ marginRight: '8px' }} />
                        Set as Initial State
                      </DropdownMenu.Item>
                      <DropdownMenu.Separator />
                    </>
                  )}

                  {!data.enabled && (
                    <>
                      <DropdownMenu.Item onClick={() => data.setEnabled(id)}>
                        <Icon name="play-circle" size="s" style={{ marginRight: '8px' }} />
                        Enable
                      </DropdownMenu.Item>
                      <DropdownMenu.Separator />
                    </>
                  )}

                  <DropdownMenu.Item onClick={() => data.openActionModal(data, id)}>
                    <Icon name="edit-alt" size="s" style={{ marginRight: '8px' }} />
                    Edit
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator />
                  <DropdownMenu.Item onClick={() => data.removeNode(id)} style={{ color: '#E74C3C' }}>
                    <Icon name="trash-alt" size="s" style={{ marginRight: '8px', color: '#E74C3C' }} />
                    Delete
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu>
            </div>
          </div>
          {data.initial && (
            <div style={{ padding: '0px 15px', paddingTop: '4px', paddingBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <span
                style={{
                  backgroundColor: '#232B36',
                  color: '#FFFFFF',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  fontSize: '11px',
                  fontWeight: '600',
                  display: 'inline-block',
                }}
              >
                Initial State
              </span>
              <Icon
                name="arrow-left"
                size="s"
                style={{
                  color: data.priority <= 1 ? '#CCCCCC' : '#023E65',
                  cursor: data.priority <= 1 ? 'not-allowed' : 'pointer',
                  opacity: data.priority <= 1 ? 0.5 : 1,
                }}
                onClick={() => {
                  if (data.priority > 1 && data.updatePriority) {
                    data.updatePriority(id, data.priority - 1);
                  }
                }}
              />
              <Tooltip position='up' title="Set Priority from 1 to 5">
                <span
                  style={{
                    fontWeight: '600',
                    fontSize: '13px',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: (() => {
                      const priority = data.priority || 1;
                      const colors: { [key: number]: string } = {
                        1: '#F46A6A',
                        2: '#E3A63D',
                        3: '#ffd992',
                        4: '#22a2ca',
                        5: '#aae6fe'
                      };
                      return colors[priority] || '#f0f0f0';
                    })(),
                    color: data.priority === 3 || data.priority === 5 ? '#000000' : '#FFFFFF'
                  }}
                >
                  {data.priority || 1}
                </span>
              </Tooltip>
              <Icon
                name="arrow-right"
                size="s"
                style={{
                  color: data.priority >= 5 ? '#CCCCCC' : '#023E65',
                  cursor: data.priority >= 5 ? 'not-allowed' : 'pointer',
                  opacity: data.priority >= 5 ? 0.5 : 1,
                }}
                onClick={() => {
                  if (data.priority < 5 && data.updatePriority) {
                    data.updatePriority(id, data.priority + 1);
                  }
                }}
              />
            </div>
          )}

          {!data.enabled && !data.initial && (
            <div style={{ padding: '0px 15px', paddingTop: '4px', paddingBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span
                style={{
                  backgroundColor: '#CCCCCC',
                  color: '#000000',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  fontSize: '11px',
                  fontWeight: '600',
                  display: 'inline-block',
                }}
              >
                Disabled
              </span>
            </div>
          )}
          <div
            className="mt-1 mb-1"
            style={{ height: '1px', backgroundColor: '#E6E6E6', margin: '0 15px' }}
          ></div>
          <div
            className="flex flex-column"
            style={{ color: 'black', padding: '4px 15px', gap: '12px' }}
          >
            {data.value && data.value.name ? (
              <>
                <div className="flex items-center" style={{ gap: '12px' }}>
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '100%',
                      backgroundColor: '#0B8FD0',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontWeight: '700', fontSize: '13px' }}>{data.value.name}</span>
                </div>
                <div className="flex items-center" style={{ gap: '12px' }}>
                  <Icon name="info" size="s" style={{ color: 'black' }} />
                  <span style={{ fontWeight: '700', fontSize: '13px' }}>
                    {data.value.type === 'CONSTANT'
                      ? 'Assignment'
                      : data.value.type === 'FUNCTION'
                        ? 'Function'
                        : data.value.type === 'PID'
                          ? 'PID Controller'
                          : data.value.type === 'TABLE'
                            ? 'Table'
                            : ''}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex flex-column" style={{ gap: '4px' }}>
                <span style={{ fontStyle: 'italic', fontWeight: 'bold' }}>
                  --Not linked--
                </span>
                <span style={{ fontStyle: 'italic', fontSize: '12px' }}>
                  Press the pencil to edit
                </span>
              </div>
            )}
          </div>
        </div>

        <Handle
          type="source"
          position={Position.Right}
          id="s3"
          style={{
            top: '30px',
            right: '-10px',
            backgroundColor: 'transparent',
            width: '10px',
            height: '10px',
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent',
            borderLeft: '10px solid #020202',
            borderRadius: '0px',
          }}
        />
        <Handle
          type="target"
          position={Position.Right}
          id="t3"
          style={{
            top: '110px',
            right: '-10px',
            backgroundColor: 'transparent',
            width: '10px',
            height: '10px',
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent',
            borderRight: '10px solid #020202',
            borderRadius: '0px',
          }}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="t4"
          style={{
            left: '70px',
            bottom: '-10px',
            backgroundColor: 'transparent',
            width: '10px',
            height: '10px',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: '10px solid #020202',
            borderRadius: '0px',
          }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="s4"
          style={{
            left: '215px',
            bottom: '-10px',
            backgroundColor: 'transparent',
            width: '10px',
            height: '10px',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '10px solid #020202',
            borderRadius: '0px',
          }}
        />
      </div>
    </>
  );
};

export default NodeComponent;
